import React, { useContext, useEffect, useRef, useState } from 'react';
import { animated, useTransition } from 'react-spring';
import Image from '../../../../components/image';
import { MessagePropertiesContext } from '../../../../contexts/messageProperties';
import { ImageTransformation, MiniEntry } from '../../../../types';
import bannerStyles from './favouritesBanner.module.pcss';

interface FavouritesBannerProps {
  product: MiniEntry | null;
  setAddedToFavourites: React.Dispatch<React.SetStateAction<MiniEntry | null>>;
}

function FavouritesBanner({ product, setAddedToFavourites }: FavouritesBannerProps): React.ReactElement {
  const [formattedMessage] = useContext(MessagePropertiesContext);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [show, setShow] = useState<MiniEntry | null>(null);
  const transitions = useTransition(show, null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 350 }
  });

  function handleHover(): void {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }

  function handleBlur(): void {
    timeoutRef.current = setTimeout(() => setShow(null), 1000);
  }

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setAddedToFavourites(null);
    }, 5000);

    setShow(product);
    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [product]);

  return (
    <>
      {transitions.map(
        ({ item, key, props }) =>
          item && (
            <animated.div key={key} style={props}>
              <div
                onMouseOver={handleHover}
                onFocus={handleHover}
                onMouseOut={handleBlur}
                onBlur={handleBlur}
                className={bannerStyles['favourites-banner']}
              >
                <Image
                  src={item.productImage}
                  alt={item.productTitle}
                  className={bannerStyles['favourites-banner__image']}
                  transformationTemplates={{
                    1: ImageTransformation.LOUNPLP_ATF,
                    2: ImageTransformation.LOUNPLP_ATF_R
                  }}
                />
                <div className={bannerStyles['favourites-banner__text']}>
                  {formattedMessage({ id: 'favourites.banner.message', fallback: 'Saved' })}
                </div>
                <a href="/wishlist" className={bannerStyles['favourites-banner__view']}>
                  {formattedMessage({ id: 'wishlist.banner.link', fallback: 'View Wishlist' })}
                </a>
              </div>
            </animated.div>
          )
      )}
    </>
  );
}

export default FavouritesBanner;
